export default class SuperCluster {
    // Public:
    constructor(cParams) {
        this.saveParams(cParams);
        this.getClusters();
        this.defineProperties();
    }

    // Private:
    saveParams(cParams) {
        this.id = cParams.id;
        this._allClusters = cParams.clusters;
        this.clusterIndices = cParams.indices;
    }

    getClusters() {
        this.clusters = [];
        let clusters = this._allClusters;
        let indices = this.clusterIndices;
        for (let i = 0; i < indices.length; i++) {
            const index = indices[i];
            const cluster = clusters[index];
            this.clusters.push(cluster);
        }
    }

    makeNodeListUnique(rawList) {
        const uniqueFunc = (value, index, self) => {
            return self.indexOf(value) === index;
        };
        let uniqueList = rawList.filter(uniqueFunc);
        uniqueList = uniqueList.sort();
        return uniqueList;
    }

    defineProperties() {
        this.defineX();
        this.defineY();
        this.defineGetterRadius();
        this.defineGetterNumberClusters();
        this.defineGetterSoloIndices();
        this.defineGetterClusterIndices();
        this.defineGetterSharedIndices();
    }

    defineX() {
        let getFunc = () => this.computeX();
        let setFunc = x => this.setCoordinateX(x);
        Object.defineProperty(this, "x", {get: getFunc, set: setFunc});
    }

    defineY() {
        let getFunc = () => this.computeY();
        let setFunc = y => this.setCoordinateY(y);
        Object.defineProperty(this, "y", {get: getFunc, set: setFunc});
    }

    defineGetterRadius() {
        let rFunc = () => this.computeRadius();
        Object.defineProperty(this, "radius", {get: rFunc});
    }

    defineGetterNumberClusters() {
        let getFunc = () => this.clusters.length;
        Object.defineProperty(this, "nClusters", {get: getFunc});
    }

    defineGetterSoloIndices() {
        let getFunc = () => this.getSoloIndices();
        Object.defineProperty(this, "soloNodesIndices", {get: getFunc});
    }

    defineGetterClusterIndices() {
        let getFunc = () => this.getClusterIndices();
        Object.defineProperty(this, "clusterNodesIndices", {get: getFunc});
    }

    defineGetterSharedIndices() {
        let getFunc = () => this.getSharedIndices();
        Object.defineProperty(this, "sharedNodesIndices", {get: getFunc});
    }

    computeRadius() {
        let externalRadius = 0;
        let x0 = this.x;
        let y0 = this.y;
        let clusters = this.clusters;
        for (let i = 0; i < clusters.length; i++) {
            const cluster = clusters[i];
            const r = cluster.radius;
            const x1 = cluster.x;
            const y1 = cluster.y;
            let u = x1 - x0;
            let v = y1 - y0;
            let d = Math.sqrt(Math.pow(u, 2) + Math.pow(v, 2));
            let R = d + r;
            if (R > externalRadius) externalRadius = R;
        }
        return externalRadius;
    }

    computeX() {
        let clusters = this.clusters;
        let x = this.computeMedianProp(clusters, "x");
        return x;
    }

    computeY() {
        let clusters = this.clusters;
        let y = this.computeMedianProp(clusters, "y");
        return y;
    }

    computeMedianProp(array, prop) {
        let propArray = [];
        for (let i = 0; i < array.length; i++) {
            const element = array[i];
            const val = element[prop];
            propArray.push(val);
        }
        let median = this.computeMedian(propArray);
        return median;
    }

    computeMedian(array) {
        let max = -Infinity;
        let min = Infinity;
        for (let i = 0; i < array.length; i++) {
            const val = array[i];
            if (val > max) max = val;
            if (val < min) min = val;
        }
        let median = (max + min) / 2;
        return median;
    }

    setCoordinateX(x) {
        let x0 = this.x;
        let dx = x - x0;
        this.translateX(dx);
    }

    setCoordinateY(y) {
        let y0 = this.y;
        let dy = y - y0;
        this.translateY(dy);
    }

    translateX(dx) {
        let clusters = this.clusters;
        for (let i = 0; i < clusters.length; i++) {
            const cluster = clusters[i];
            cluster.x += dx;
        }
    }

    translateY(dy) {
        let clusters = this.clusters;
        for (let i = 0; i < clusters.length; i++) {
            const cluster = clusters[i];
            cluster.y += dy;
        }
    }

    getSoloIndices() {
        return this.getNodeIndices("solo");
    }

    getClusterIndices() {
        return this.getNodeIndices("cluster");
    }

    getSharedIndices() {
        return this.getNodeIndices("shared");
    }

    getNodeIndices(category) {
        const field = category + "Nodes";
        let list = [];
        let clusters = this.clusters;
        clusters.forEach(cluster => {
            let indices = cluster[field];
            list.selfConcat(indices);
        });
        list = this.makeNodeListUnique(list);
        return list;
    }
}
